type ScopeAccess = 'read' | 'write'

interface ScopeCopy {
  read: string
  write: string
  readWrite: string
}

// Rows render in this order.
const scopeResources = [
  'conversations',
  'calls',
  'contacts',
  'account',
  'agents',
] as const

type ScopeResource = (typeof scopeResources)[number]

const scopeCopy: Record<ScopeResource, ScopeCopy> = {
  conversations: {
    read: 'Read your conversations',
    write: 'Send messages on your behalf',
    readWrite: 'Read your conversations and send messages',
  },
  calls: {
    read: 'See your AI agent calls',
    write: 'Start and cancel AI agent calls on your behalf',
    readWrite: 'See, start and cancel AI agent calls on your behalf',
  },
  contacts: {
    read: 'See your contacts',
    write: 'Edit, create and delete your contacts',
    readWrite: 'See, edit, create and delete your contacts',
  },
  account: {
    read: 'See your account and workspace information',
    write: 'Edit your account and workspace information',
    readWrite: 'See and edit your account and workspace information',
  },
  agents: {
    read: "See your AI agents' configurations",
    write: "Edit your AI agents' configurations",
    readWrite: "See and edit your AI agents' configurations",
  },
}

function isScopeAccess(value: string): value is ScopeAccess {
  return value === 'read' || value === 'write'
}

function isScopeResource(value: string): value is ScopeResource {
  return Object.hasOwn(scopeCopy, value)
}

function parseScope(
  scope: string,
): { access: ScopeAccess; resource: ScopeResource } | null {
  const [access, resource, ...rest] = (
    scope === 'profile' ? 'read:account' : scope
  ).split(':')

  if (!access || !resource || rest.length > 0) {
    return null
  }

  return isScopeAccess(access) && isScopeResource(resource) ? { access, resource } : null
}

export function scopeList(scope: string): string[] {
  const granted = new Map<ScopeResource, Set<ScopeAccess>>()
  // Every requested scope is granted on Allow, so anything without copy is
  // still shown rather than dropped.
  const unrecognised = new Set<string>()

  for (const requested of scope.split(/[\s,]+/).filter(Boolean)) {
    const parsed = parseScope(requested)

    if (parsed) {
      granted.set(
        parsed.resource,
        (granted.get(parsed.resource) ?? new Set()).add(parsed.access),
      )
    } else {
      unrecognised.add(requested)
    }
  }

  const list = scopeResources.flatMap((resource) => {
    const access = granted.get(resource)

    if (!access) {
      return []
    }

    const copy = scopeCopy[resource]

    return [
      access.has('read') && access.has('write')
        ? copy.readWrite
        : access.has('read')
          ? copy.read
          : copy.write,
    ]
  })

  if (unrecognised.size > 0) {
    list.push(`Other permissions: ${[...unrecognised].join(', ')}`)
  }

  return list
}
